/**
 * @file Hooks SWR et Axios pour la gestion de la configuration de services restaurant.
 * @module common.hooks.api.app.restaurants.Services
 */

import {useState} from 'react';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useSWRConfig} from 'swr';
import {useSnackbar} from 'packages/common/components/snackbar';
import {useSWRWithSync} from 'packages/common/hooks/useSWRWithSync';
import {APP_EVENTS, notifyRestaurantServicesUpdated} from 'packages/common/utils/appEventSync';
import {useActiveCompany, usePermissions} from 'packages/common/hooks/session';
import {isReviewRequestEnabled} from 'packages/common/constants/restaurants/reviewRequests';

/**
 * Récupère la configuration de services via SWR avec synchronisation cross-onglet.
 *
 * @param {string} companyId - Identifiant de l'établissement
 * @return {Object} { config, isLoading, isError, isValidating, mutate }
 */
export const useGetServices = (companyId) => {
  const {enqueueSnackbar} = useSnackbar();
  const endpointUrl = companyId ? endpoints.app.restaurant.services.get(companyId) : null;

  const fetcher = async (url) => {
    try {
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 403) {
        enqueueSnackbar('Vous n\'avez pas les droits pour accéder à la configuration', {variant: 'error'});
      } else if (error?.response?.status === 404) {
        return null;
      } else {
        enqueueSnackbar('Erreur lors de la récupération de la configuration', {variant: 'error'});
      }
      throw error;
    }
  };

  const {data, error, mutate, isValidating} = useSWRWithSync(endpointUrl, fetcher, {
    eventTypes: APP_EVENTS.RESTAURANT.SERVICES_UPDATED,
    companyId,
  });

  return {
    config: data,
    isLoading: !error && data === undefined,
    isError: !!error,
    isValidating,
    mutate,
  };
};

/**
 * Indique si la demande d'avis restaurant est activée pour l'établissement actif.
 *
 * Utilisable hors du module restaurant (fiche client, wellness) : sans accès au
 * module, la configuration n'est pas chargée et le hook renvoie false. Avec
 * accès, la lecture partage le cache SWR déjà alimenté par la navigation.
 *
 * @return {boolean} Vrai si une demande d'avis peut partir
 */
export const useReviewRequestsEnabled = () => {
  const {companyId} = useActiveCompany();
  const {hasModuleAccess} = usePermissions();
  const {config} = useGetServices(hasModuleAccess('restaurant') ? companyId : null);

  return isReviewRequestEnabled(config);
};

/**
 * Crée une nouvelle configuration de services.
 *
 * @return {Object} { createConfig, loading, error }
 */
export const useCreateServices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {enqueueSnackbar} = useSnackbar();

  const createConfig = async (companyId, data) => {
    setLoading(true);
    setError(null);

    try {
      const configData = prepareConfigData(data);

      const response = await axiosInstance.post(
          endpoints.app.restaurant.services.create(companyId),
          configData,
      );

      notifyRestaurantServicesUpdated(companyId, {action: 'create'});

      return response.data;
    } catch (error) {
      setError(error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la création de la configuration';
      enqueueSnackbar(errorMessage, {variant: 'error'});
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {createConfig, loading, error};
};

/**
 * Met à jour la configuration de services.
 *
 * @return {Object} { updateConfig, loading, error }
 */
export const useUpdateServices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {enqueueSnackbar} = useSnackbar();
  const {mutate} = useSWRConfig();

  const updateConfig = async (companyId, data) => {
    setLoading(true);
    setError(null);

    try {
      const configData = prepareConfigData(data);

      const response = await axiosInstance.put(
          endpoints.app.restaurant.services.update(companyId),
          configData,
      );

      const endpointUrl = endpoints.app.restaurant.services.get(companyId);
      await mutate(endpointUrl);

      notifyRestaurantServicesUpdated(companyId, {action: 'update'});

      return response.data;
    } catch (error) {
      setError(error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour de la configuration';
      enqueueSnackbar(errorMessage, {variant: 'error'});
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {updateConfig, loading, error};
};

/**
 * Retourne une fonction impérative pour interroger le statut d'annulation d'une réservation.
 *
 * Détermine si l'annulation est tardive (délai dépassé) ou dans les délais selon
 * la politique configurée. Conçu pour un usage ponctuel (ex: ouverture d'une modale) —
 * pas de cache SWR, pas de snackbar d'erreur (l'appelant gère le fail-silently).
 *
 * @return {Function} getCancellationStatus(companyId, reservationId) → Promise<Object>
 */
export const useGetCancellationStatus = () => {
  const getCancellationStatus = async (companyId, reservationId) => {
    const response = await axiosInstance.get(
        endpoints.app.restaurant.reservation.cancellationStatus(companyId, reservationId),
    );
    return response.data;
  };
  return getCancellationStatus;
};

/**
 * Prépare les données de configuration avant envoi à l'API.
 * Convertit les champs numériques en entiers et supprime les champs internes (companyId).
 *
 * @param {Object} data - Données brutes du formulaire
 * @return {Object} Données normalisées prêtes pour l'API
 */
const prepareConfigData = (data) => {
  const configData = {...data};

  delete configData.companyId;

  if (configData.reservationConstraints) {
    if (configData.reservationConstraints.minGuests !== undefined) {
      configData.reservationConstraints.minGuests =
          parseInt(configData.reservationConstraints.minGuests, 10);
    }
    if (configData.reservationConstraints.onlineBooking?.maxGuests !== undefined) {
      configData.reservationConstraints.onlineBooking.maxGuests =
          parseInt(configData.reservationConstraints.onlineBooking.maxGuests, 10);
    }
  }

  if (configData.bookingPolicy) {
    if (configData.bookingPolicy.advanceBookingDays !== undefined) {
      configData.bookingPolicy.advanceBookingDays =
          parseInt(configData.bookingPolicy.advanceBookingDays, 10);
    }
    if (configData.bookingPolicy.minimumNoticeHours !== undefined) {
      // parseFloat (et non parseInt) pour préserver les délais décimaux (ex. 1.5 = 1h30)
      // saisis via le sélecteur Jours / Heures / Minutes.
      configData.bookingPolicy.minimumNoticeHours =
          parseFloat(configData.bookingPolicy.minimumNoticeHours);
    }
  }

  if (configData.cancellationPolicy) {
    if (configData.cancellationPolicy.minimumNoticeHours !== undefined) {
      configData.cancellationPolicy.minimumNoticeHours =
          parseFloat(configData.cancellationPolicy.minimumNoticeHours);
    }
  }

  if (configData.cardHoldPolicy) {
    if (configData.cardHoldPolicy.flatAmount !== undefined) {
      configData.cardHoldPolicy.flatAmount = parseInt(configData.cardHoldPolicy.flatAmount, 10);
    }
    if (configData.cardHoldPolicy.perGuestAmount !== undefined) {
      configData.cardHoldPolicy.perGuestAmount = parseInt(configData.cardHoldPolicy.perGuestAmount, 10);
    }
    if (configData.cardHoldPolicy.minimumGuests !== undefined) {
      configData.cardHoldPolicy.minimumGuests = parseInt(configData.cardHoldPolicy.minimumGuests, 10);
    }
  }

  return configData;
};
